import { TreatmentRecord } from '../types/treatment';
import logger from '../utils/logger';
import { TreatmentService } from './treatmentService';

export interface CustomerInsight {
  usagePattern: {
    monthlyVisitAverage: number;
    daysSinceLastVisit: number;
    topMenus: Array<{ name: string; count: number }>;
    visitTrend: 'increasing' | 'stable' | 'decreasing';
  };
  salesAnalysis: {
    totalRevenue: number;
    averagePerVisit: number;
    lastThreeMonthsAverage: number;
    lifetimeValue: number;
  };
  customerStatus: {
    engagementLevel: 'new_customer' | 'active' | 'core' | 'dormant';
    repeatRate: number;
    lastVisitDate: Date | null;
    totalVisits: number;
  };
  recommendations: {
    nextVisitPrediction: string;
    suggestedServices: string[];
    riskLevel: 'low' | 'medium' | 'high';
  };
  treatmentRecords?: Array<{
    id: string;
    treatmentDate: string;
    totalAmount: number;
    treatmentMenu: string;
    startTime?: string;
  }>;
}

export class CustomerInsightService {
  // 日付を安全にDateオブジェクトに変換するヘルパー関数
  private static toDate(date: Date | string | number | unknown): Date {
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string') {
      return new Date(date);
    }
    if (typeof date === 'number') {
      return new Date(date);
    }
    // unknown型の場合は現在時刻を返す
    return new Date();
  }
  
  static async calculateInsights(customerId: number): Promise<CustomerInsight | null> {
    try {
      const records = await TreatmentService.getTreatmentRecords(customerId);
      
      if (!records || records.length === 0) {
        return null;
      }
      
      // 日付でソート（新しい順）
      const sortedRecords = [...records].sort((a, b) => {
        const dateA = this.toDate(a.date);
        const dateB = this.toDate(b.date);
        return dateB.getTime() - dateA.getTime();
      });
      
      // 利用傾向分析の計算
      const usagePattern = this.calculateUsagePattern(sortedRecords);
      
      // 売上分析の計算
      const salesAnalysis = this.calculateSalesAnalysis(sortedRecords);
      
      // 顧客ステータスの計算
      const customerStatus = this.calculateCustomerStatus(sortedRecords);
      
      // レコメンドの生成（Phase 1では簡易版）
      const recommendations = this.generateRecommendations(sortedRecords);
      
      // 施術記録データを追加（グラフ表示用）
      const treatmentRecords = sortedRecords.map(r => {
        const dateValue = this.toDate(r.date);
        
        return {
          id: r.id,
          treatmentDate: dateValue.toISOString(),
          totalAmount: r.totalAmount,
          treatmentMenu: r.treatmentMenu,
          startTime: undefined // TreatmentRecordにstartTimeがないため
        };
      });
      
      return {
        usagePattern,
        salesAnalysis,
        customerStatus,
        recommendations,
        treatmentRecords
      };
    } catch (error) {
      logger.error('Failed to calculate customer insights:', error);
      return null;
    }
  }
  
  private static calculateUsagePattern(records: TreatmentRecord[]) {
    // 月平均来店回数の計算
    const firstVisit = this.toDate(records[records.length - 1].date);
    const lastVisit = this.toDate(records[0].date);
    const monthsDiff = Math.max(1, 
      (lastVisit.getTime() - firstVisit.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    const monthlyVisitAverage = Math.round((records.length / monthsDiff) * 10) / 10;
    
    // 前回来店からの日数
    const daysSinceLastVisit = Math.floor(
      (new Date().getTime() - lastVisit.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    // 人気メニューTOP3
    const menuCount = new Map<string, number>();
    records.forEach(record => {
      const menu = record.treatmentMenu;
      menuCount.set(menu, (menuCount.get(menu) || 0) + 1);
    });
    
    const topMenus = Array.from(menuCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({ name, count }));
    
    // 来店トレンドの判定（簡易版）
    let visitTrend: 'increasing' | 'stable' | 'decreasing' = 'stable';
    if (records.length >= 3) {
      const recentMonths = 3;
      const recentDate = new Date();
      recentDate.setMonth(recentDate.getMonth() - recentMonths);
      const recentRecords = records.filter(r => this.toDate(r.date) >= recentDate);
      const recentAverage = recentRecords.length / recentMonths;
      
      if (recentAverage > monthlyVisitAverage * 1.2) {
        visitTrend = 'increasing';
      } else if (recentAverage < monthlyVisitAverage * 0.8) {
        visitTrend = 'decreasing';
      }
    }
    
    return {
      monthlyVisitAverage,
      daysSinceLastVisit,
      topMenus,
      visitTrend
    };
  }
  
  private static calculateSalesAnalysis(records: TreatmentRecord[]) {
    // 累計売上額
    const totalRevenue = records.reduce((sum, record) => 
      sum + (record.totalAmount || 0), 0
    );
    
    // 平均客単価
    const averagePerVisit = Math.round(totalRevenue / records.length);
    
    // 直近3ヶ月の平均客単価
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const recentRecords = records.filter(r => this.toDate(r.date) >= threeMonthsAgo);
    const lastThreeMonthsAverage = recentRecords.length > 0
      ? Math.round(recentRecords.reduce((sum, r) => sum + (r.totalAmount || 0), 0) / recentRecords.length)
      : 0;
    
    // LTV（簡易計算）
    const lifetimeValue = totalRevenue;
    
    return {
      totalRevenue,
      averagePerVisit,
      lastThreeMonthsAverage,
      lifetimeValue
    };
  }
  
  private static calculateCustomerStatus(records: TreatmentRecord[]) {
    // エンゲージメントレベルの判定
    const lastVisitDate = records.length > 0 ? this.toDate(records[0].date) : null;
    const totalVisits = records.length;
    
    const engagementLevel = this.calculateEngagementLevel(totalVisits, lastVisitDate);
    
    // リピート率の計算（継続来店率として算出）
    // 初回来店から現在までの月数と、実際に来店があった月数の比率
    const visitsByMonth = new Map<string, number>();
    records.forEach(record => {
      const date = this.toDate(record.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      visitsByMonth.set(monthKey, (visitsByMonth.get(monthKey) || 0) + 1);
    });
    
    const monthsWithVisit = visitsByMonth.size;
    const firstVisit = this.toDate(records[records.length - 1].date);
    const lastVisit = this.toDate(records[0].date);
    
    // 初回来店から最終来店までの月数を正確に計算
    const yearDiff = lastVisit.getFullYear() - firstVisit.getFullYear();
    const monthDiff = lastVisit.getMonth() - firstVisit.getMonth();
    const totalMonths = Math.max(1, yearDiff * 12 + monthDiff + 1);
    
    // リピート率は最大100%に制限
    const repeatRate = Math.min(100, Math.round((monthsWithVisit / totalMonths) * 100));
    
    return {
      engagementLevel,
      repeatRate,
      lastVisitDate,
      totalVisits
    };
  }
  
  private static calculateEngagementLevel(totalVisits: number, lastVisitDate: Date | null): 'new_customer' | 'active' | 'core' | 'dormant' {
    if (!lastVisitDate) return 'new_customer';
    
    const daysSinceLastVisit = Math.floor((new Date().getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // 新規顧客: 初回来店のみまたは30日以内で来店回数が少ない
    if (totalVisits === 1 || (daysSinceLastVisit <= 30 && totalVisits <= 3)) {
      return 'new_customer';
    }
    
    // 休眠顧客: 60日以上来店なし
    if (daysSinceLastVisit > 60) {
      return 'dormant';
    }
    
    // コア顧客: 5回以上来店かつ60日以内に来店
    if (totalVisits >= 5 && daysSinceLastVisit <= 60) {
      return 'core';
    }
    
    // アクティブ顧客: その他の定期来店者
    return 'active';
  }
  
  private static generateRecommendations(records: TreatmentRecord[]) {
    // 次回来店予測（簡易版）
    const avgDaysBetweenVisits = this.calculateAverageDaysBetweenVisits(records);
    const daysSinceLastVisit = Math.floor(
      (new Date().getTime() - this.toDate(records[0].date).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    let nextVisitPrediction = '予測中...';
    if (avgDaysBetweenVisits > 0) {
      const predictedDays = Math.max(0, avgDaysBetweenVisits - daysSinceLastVisit);
      if (predictedDays === 0) {
        nextVisitPrediction = 'いつでも';
      } else if (predictedDays <= 7) {
        nextVisitPrediction = '1週間以内';
      } else if (predictedDays <= 14) {
        nextVisitPrediction = '2週間以内';
      } else if (predictedDays <= 30) {
        nextVisitPrediction = '1ヶ月以内';
      } else {
        nextVisitPrediction = `約${Math.round(predictedDays / 30)}ヶ月後`;
      }
    }
    
    // 離脱リスクレベル
    let riskLevel: 'low' | 'medium' | 'high' = 'low';
    if (daysSinceLastVisit > avgDaysBetweenVisits * 2) {
      riskLevel = 'high';
    } else if (daysSinceLastVisit > avgDaysBetweenVisits * 1.5) {
      riskLevel = 'medium';
    }
    
    // おすすめサービスの生成（メニュー履歴から分析）
    const suggestedServices = this.generateServiceSuggestions(records);
    
    return {
      nextVisitPrediction,
      suggestedServices,
      riskLevel
    };
  }
  
  private static generateServiceSuggestions(records: TreatmentRecord[]): string[] {
    if (records.length === 0) return ['初回限定コースをお試しください'];
    
    // メニューの利用頻度を分析
    const menuFrequency = new Map<string, number>();
    records.forEach(record => {
      const menu = record.treatmentMenu;
      menuFrequency.set(menu, (menuFrequency.get(menu) || 0) + 1);
    });
    
    // 最も利用されているメニューを取得
    const topMenu = Array.from(menuFrequency.entries())
      .sort((a, b) => b[1] - a[1])[0]?.[0];
    
    // 最後に利用したメニュー（将来の拡張用）
    // const lastMenu = records[0].treatmentMenu;
    
    // サジェスト生成ロジック
    const suggestions: string[] = [];
    
    // 60分コースを利用している場合は90分を提案
    if (topMenu?.includes('60分')) {
      suggestions.push('90分コースでより深いリラクゼーションを');
    }
    
    // ドライヘッドスパ利用者にアロマを提案
    if (topMenu?.includes('ドライヘッドスパ') && !topMenu.includes('アロマ')) {
      suggestions.push('アロマヘッドスパで香りの癒しをプラス');
    }
    
    // アロマ利用者に季節限定を提案
    if (topMenu?.includes('アロマ')) {
      const month = new Date().getMonth();
      if (month >= 2 && month <= 4) {
        suggestions.push('春限定 桜の香りのアロマコース');
      } else if (month >= 5 && month <= 7) {
        suggestions.push('夏限定 ミントの爽やかアロマコース');
      } else if (month >= 8 && month <= 10) {
        suggestions.push('秋限定 金木犀の香りアロマコース');
      } else {
        suggestions.push('冬限定 柚子の温かアロマコース');
      }
    }
    
    // 頻度が高い顧客には会員特典を提案
    if (records.length >= 10) {
      suggestions.push('プレミアム会員限定の特別コース');
    }
    
    // デフォルトの提案
    if (suggestions.length === 0) {
      suggestions.push('人気No.1 ドライヘッドスパ60分コース');
      suggestions.push('お得な回数券のご案内');
    }
    
    return suggestions.slice(0, 3); // 最大3つまで
  }
  
  private static calculateAverageDaysBetweenVisits(records: TreatmentRecord[]): number {
    if (records.length < 2) return 30; // デフォルト値
    
    let totalDays = 0;
    for (let i = 0; i < records.length - 1; i++) {
      const days = Math.floor(
        (this.toDate(records[i].date).getTime() - this.toDate(records[i + 1].date).getTime()) 
        / (1000 * 60 * 60 * 24)
      );
      totalDays += days;
    }
    
    return Math.round(totalDays / (records.length - 1));
  }
}